import React from "react";
import { createPortal } from "react-dom";

import { fetchDispatchGeotabCheckTrace } from "../../api/geotabApi.js";
import { fallbackScale } from "../../utils/dispatchGeotabGap.js";
import { CHECK_CASES } from "../../utils/dispatchGeotabCheck.js";
import { fmtTime } from "../geotab/geotabFormat.js";
import { DispatchGeotabOrdersBanner, DispatchGeotabOrdersPanel } from "./DispatchGeotabOrders.jsx";
import DispatchGeotabPlaceCard from "./DispatchGeotabPlaceCard.jsx";
import { mountGapMap } from "./dispatchGeotabGapMap.js";
import { mountGapPlayer } from "./dispatchGeotabGapPlayer.js";
import "./DispatchGeotabGapView.css";

// « Voir » un écart Geotab : la ligne du camion telle qu'au Gantt et sa journée Geotab dessous, rejouée avec un arrêt sur
// chaque différence avec le Prévu, et la carte de la journée (route prévue, trace GPS, point de l'arrêt hors du Dispatch).
// Ouverte par l'épingle du haut ou « Clore la journée » : la liste Geotab de la journée, claire, une ligne par écart ; « Voir »
// passe au lecteur du camion, foncé, « ‹ Liste » revient à la liste. Pour « Clore la journée », la liste porte en plus la
// question de fermeture et ses boutons.

function sourceTrack(truckId) {
  const id = window.CSS?.escape ? window.CSS.escape(String(truckId)) : String(truckId);
  return document.querySelector(`[data-fleet-timeline-row="true"][data-assignment-id="${id}"][data-dispatch-period="day"] [data-fleet-timeline-track="true"]`);
}

function scaleOf(track) {
  const span = Number(track?.dataset?.timelineScale);
  return span > 0 ? { start: Number(track.dataset.timelineStart) || 0, span, lead: Number(track.dataset.timelineLead) || 0, trail: Number(track.dataset.timelineTrail) || 0 } : null;
}

const longDate = (iso) => {
  const at = new Date(`${iso}T12:00:00`);
  return Number.isNaN(at.getTime()) ? "" : new Intl.DateTimeFormat("fr-CA", { weekday: "long", day: "numeric", month: "long" }).format(at);
};

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function GapPlayer({ item, day, date, query, playerRef }) {
  const hostRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const mapViewRef = React.useRef(null);
  const traceRef = React.useRef(null);
  const scrubRef = React.useRef(null);
  const clockRef = React.useRef(null);
  const lastTRef = React.useRef(null);
  const [width, setWidth] = React.useState(0);
  const [playing, setPlayingState] = React.useState(false);
  const playingRef = React.useRef(false);
  const setPlaying = React.useCallback((value) => { playingRef.current = value; setPlayingState(value); }, []);
  const [traceState, setTraceState] = React.useState(query ? "loading" : "none");

  // La largeur de la fenêtre change : les lignes sont refaites au même instant de la relecture.
  React.useLayoutEffect(() => {
    const host = hostRef.current;
    const observer = new ResizeObserver(() => setWidth((w) => (Math.abs(w - host.clientWidth) > 4 ? host.clientWidth : w)));
    observer.observe(host);
    setWidth(host.clientWidth);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const mapView = mountGapMap(mapRef.current, { day, item });
    mapViewRef.current = mapView;
    if (traceRef.current) mapView?.setTrace(traceRef.current);
    return () => { mapView?.destroy(); mapViewRef.current = null; };
  }, [day, item]);

  // Tracé GPS daté, sur la même lecture de Geotab que le contrôle.
  React.useEffect(() => {
    if (!query) return undefined;
    const controller = new AbortController();
    fetchDispatchGeotabCheckTrace(date, item.truck_id, query.limits, query.revision, { signal: controller.signal })
      .then((data) => { traceRef.current = data.points || []; mapViewRef.current?.setTrace(traceRef.current); setTraceState(traceRef.current.length ? "ready" : "empty"); })
      .catch(() => { if (!controller.signal.aborted) setTraceState("error"); });
    return () => controller.abort();
  }, [date, item.truck_id, query]);

  React.useEffect(() => {
    if (!width) return undefined;
    const track = sourceTrack(item.truck_id);
    const first = lastTRef.current == null;
    let player = null;
    player = mountGapPlayer(hostRef.current, {
      item, day, track, scale: scaleOf(track) || fallbackScale(day), startAt: lastTRef.current,
      onTime: (t, _pending, [a, b]) => {
        lastTRef.current = t;
        mapViewRef.current?.setTime(t);
        if (scrubRef.current) scrubRef.current.value = String(b > a ? Math.round(((t - a) / (b - a)) * 1000) : 1000);
        if (clockRef.current) clockRef.current.textContent = fmtTime(t);
      },
      onEvent: (ev) => mapViewRef.current?.focus(ev),
      onResume: () => mapViewRef.current?.unfocus(),
      onEnd: () => { mapViewRef.current?.end(); setPlaying(false); },
    });
    playerRef.current = player;
    // Première ouverture : la relecture part seule ; lignes refaites : elle reprend où elle en était.
    if (first && reducedMotion()) player.skip();
    else if (first || playingRef.current) { player.play(); setPlaying(true); }
    return () => { player.destroy(); playerRef.current = null; };
  }, [width, item, day, playerRef, setPlaying]);

  const toggle = React.useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (player.playing) { player.pause(); setPlaying(false); } else { player.play(); setPlaying(true); }
  }, [playerRef, setPlaying]);
  // Espace : pause, puis reprise au même instant, y compris pendant un arrêt sur une différence.
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== " ") return;
      event.preventDefault();
      event.stopPropagation();
      toggle();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [toggle]);
  const seek = (event) => {
    const player = playerRef.current;
    if (!player) return;
    const [a, b] = player.range;
    player.seek(a + (Number(event.target.value) / 1000) * (b - a));
    setPlaying(false);
  };
  // Un clic dans les lignes : passer l'arrêt en cours, sinon aller à la fin.
  const onLanesClick = () => { const player = playerRef.current; if (player?.holding) player.continue(); else if (player?.playing) { player.skip(); setPlaying(false); } };

  return (
    <>
      <div ref={hostRef} className="gg-host" onClick={onLanesClick} />
      <div className="relative mt-3">
        <div ref={mapRef} className="gg-map h-[220px] sm:h-[300px]" />
        {traceState === "loading" ? <p className="gg-map-note" role="status">Lecture du tracé GPS…</p> : null}
        {traceState === "error" || traceState === "empty" ? <p className="gg-map-note">{traceState === "error" ? "Tracé GPS indisponible : la carte montre les arrêts." : "Aucun tracé GPS pour cette journée."}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <button type="button" onClick={(event) => { event.stopPropagation(); toggle(); }} aria-label={playing ? "Pause" : "Lecture"} title="Espace"
          className="inline-flex h-8 w-9 items-center justify-center rounded-[4px] border border-[#3f5585] bg-[#3f5585] text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
          {playing ? "❚❚" : "▶"}
        </button>
        <input ref={scrubRef} type="range" min="0" max="1000" defaultValue="0" onInput={seek} aria-label="Moment de la journée" className="min-w-40 flex-1 accent-[#8fa8e0]" />
        <span ref={clockRef} className="text-xs tabular-nums text-[#9aa6b8]">{fmtTime(day.real_departure)}</span>
        <span className="gg-legend"><span><i className="gg-legend-plan" />Prévu</span><span><i />Fait (Geotab)</span><span><i className="gg-legend-hatch" />Prévu, pas fait</span><span><i className="gg-legend-arrow" />Hors du Dispatch</span></span>
      </div>
    </>
  );
}

// data : la liste Geotab de la journée (écarts, commandes, camions, journées) ; items : ses écarts, dans l'ordre du lecteur.
export default function DispatchGeotabGapView({ data = null, items = [], date = "", query = null, index = 0, initialTab = "trucks", closing = null, onIndex, onClose }) {
  // list : la liste de la journée (épingle du haut, « Clore la journée ») ; sinon le lecteur d'un camion (compteur du Gantt,
  // « Voir l'écart »). other : camion sans écart ouvert par « Voir la journée ».
  const [list, setList] = React.useState(initialTab === "orders");
  const [other, setOther] = React.useState(null);
  const [placeId, setPlaceId] = React.useState(null);
  const [saved, setSaved] = React.useState("");
  const modalRef = React.useRef(null);
  const placeOrigin = React.useRef(null);
  const place = (data?.orders?.items || []).find((order) => order.id === placeId);
  const showingList = list && !place;
  React.useLayoutEffect(() => {
    if (placeId == null && placeOrigin.current) {
      if (modalRef.current) modalRef.current.scrollTop = placeOrigin.current.scroll;
      (placeOrigin.current.button?.isConnected ? placeOrigin.current.button : closeRef.current)?.focus({ preventScroll: true });
      placeOrigin.current = null;
    }
  }, [placeId]);
  const item = React.useMemo(() => (other ? { ...other, case: "", stops: [] } : items[index] || null), [other, items, index]);
  const closeRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const returnToListRef = React.useRef(false);
  const showList = React.useCallback(() => { setList(true); setOther(null); returnToListRef.current = false; }, []);
  const closeCurrent = React.useCallback(() => {
    if (placeId != null) setPlaceId(null);
    else if (!list && returnToListRef.current) showList();
    else onClose();
  }, [list, onClose, showList, placeId]);
  React.useEffect(() => { closeRef.current?.focus({ preventScroll: true }); }, [list]);
  React.useEffect(() => {
    // Au-dessus de tout : Échap revient, les flèches passent d'un écart à l'autre (Espace : voir GapPlayer).
    const onKeyDown = (event) => {
      const browsing = !list && !other;
      if (event.key === "Escape") closeCurrent();
      else if (browsing && event.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      else if (browsing && event.key === "ArrowRight" && index < items.length - 1) onIndex(index + 1);
      else return;
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [index, items.length, closeCurrent, onIndex, list, other]);
  const day = React.useMemo(() => (item ? (data?.days || []).find((entry) => entry.truck_id === item.truck_id)
    || { sites: [], visits: (item.stops || []).map((stop) => ({ arr: stop.arr, dep: stop.dep, kind: "stop", name: stop.place })) } : null), [data, item]);
  if (!list && !item) return null;
  const openTruck = (truckId) => {
    returnToListRef.current = true;
    const at = items.findIndex((entry) => entry.truck_id === truckId);
    const truck = (data?.trucks || []).find((entry) => entry.truck_id === truckId);
    setOther(at < 0 && truck ? { truck_id: truck.truck_id, truck_name: truck.truck_name, driver: truck.driver } : null);
    if (at >= 0) onIndex(at);
    setList(false);
  };
  const kind = item?.case ? CHECK_CASES[item.case] || CHECK_CASES.none : null;
  return createPortal(
    <div className={`gg-veil font-sans${showingList ? " gg-light" : ""}`} data-grain-dialog-foreground="true" data-dispatch-geotab-gap="true" onMouseDown={(event) => { if (event.target === event.currentTarget) closeCurrent(); }}>
      <div ref={modalRef} className="gg-modal !rounded-[4px]" role="dialog" aria-modal="true" aria-label={place ? `Carte Geotab · ${place.client}` : list ? "Geotab de la journée" : `Écart Geotab · ${item.truck_name}`}>
        <div className="gg-bar flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
          {list ? <p className="m-0 text-[0.82rem]"><b className="mr-1 text-base">{closing?.title || "Geotab"}</b>{closing ? null : `${longDate(date)} · Jour`}</p> : (
            <p className="m-0 min-w-0 text-[0.82rem]">
              <b className="mr-1 text-base">{item.truck_name}</b>{[item.driver, longDate(date)].filter(Boolean).join(" · ")}
              {kind ? <span className={`ml-2 inline-block rounded-[4px] px-1.5 text-[0.7rem] font-bold leading-[19px] ${kind.className}`}>{kind.label}</span> : null}
            </p>
          )}
          <div className="ml-auto flex items-center gap-2 text-[0.8rem]">
            {!list && !other && items.length > 1 ? (
              <>
                <button type="button" className="gg-btn" disabled={index === 0} onClick={() => onIndex(index - 1)} aria-label="Écart précédent">‹</button>
                <span className="tabular-nums">Écart {index + 1} sur {items.length}</span>
                <button type="button" className="gg-btn" disabled={index === items.length - 1} onClick={() => onIndex(index + 1)} aria-label="Écart suivant">›</button>
              </>
            ) : null}
            <button ref={closeRef} type="button" className="gg-btn" onClick={closeCurrent} aria-label="Fermer">✕ <kbd className="ml-1 text-[10px] font-normal opacity-70">Échap</kbd></button>
          </div>
        </div>
        {list ? <div hidden={Boolean(place)}><DispatchGeotabOrdersPanel data={data} gaps={items} date={date} onOpen={openTruck} saved={saved}
          onOpenPlace={(order, button) => {
            placeOrigin.current = { button, scroll: modalRef.current?.scrollTop || 0 };
            setPlaceId(order.id);
            setSaved("");
            if (modalRef.current) modalRef.current.scrollTop = 0;
          }} /></div>
          : <DispatchGeotabOrdersBanner orders={data?.orders?.items || []} truckName={item.truck_name} onBack={showList} />}
        {place ? <DispatchGeotabPlaceCard key={place.id} order={place} date={date} onSaved={setSaved} saved={saved} onBack={() => setPlaceId(null)} /> : null}
        {list ? null
          : <GapPlayer key={`${other ? "journee" : index}:${item.truck_id}`} item={item} day={day} date={date} query={query} playerRef={playerRef} />}
        {showingList && closing ? (
          <div className="sticky bottom-0 flex justify-end gap-2.5 border-t border-slate-200 bg-white px-4 py-2.5" data-dispatch-geotab-closing="true">
            <button type="button" onClick={onClose}
              className="rounded-[4px] border border-slate-300 bg-white px-3.5 py-1.5 text-[0.86rem] font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">Revenir au Dispatch</button>
            <button type="button" onClick={closing.confirm}
              className="rounded-[4px] border border-[var(--color-border-warning-emphasis)] bg-amber px-3.5 py-1.5 text-[0.86rem] font-semibold text-white hover:bg-[var(--color-alert-warning-ink)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber/35">Clore quand même</button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
